import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useApi, apiPost, apiPut, apiDelete } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";
import { LoadingOverlay } from "./LoadingPanel.tsx";

interface ScopeItem {
  name: string;
  description: string;
  assumptions: string[];
  estimateLowHours: number;
  estimateHighHours: number;
}

interface Draft {
  id: number;
  recordingId: string;
  recordingTitle: string | null;
  projectId: number | null;
  status: "draft" | "sent";
  subject: string | null;
  intro: string | null;
  scopeItems: ScopeItem[];
  outOfScope: string[];
  closing: string | null;
  signOffAsk: string | null;
  totalLowHours: number | null;
  totalHighHours: number | null;
  recipientEmails: string[];
  tone: string;
  includeEstimate: boolean;
  confidence: string | null;
  warnings: string[];
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DraftsResponse {
  drafts: Draft[];
}

interface Recording {
  id: string;
  title: string | null;
  started_at: string;
}

interface RecordingsResponse {
  recordings: Recording[];
  cursor: string | null;
  hasMore: boolean;
}

interface Project {
  id: number;
  name: string;
}

export function ScopingEmails() {
  const { data: draftsData, refetch: refetchDrafts } = useApi<DraftsResponse>("/api/scoping-emails");
  const { data: recordingsPage } = useApi<RecordingsResponse>("/api/transcripts");
  const { data: projects } = useApi<Project[]>("/api/projects");

  const [editing, setEditing] = useState<Draft | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [generateOptions, setGenerateOptions] = useState<{
    projectId: number;
    includeEstimate: boolean;
  }>({ projectId: 0, includeEstimate: true });
  const [error, setError] = useState<string | null>(null);

  const drafts = draftsData?.drafts || [];
  const recordings = recordingsPage?.recordings || [];

  const draftByRecording = useMemo(() => {
    const map = new Map<string, Draft>();
    for (const d of drafts) {
      const existing = map.get(d.recordingId);
      if (!existing || new Date(d.updatedAt) > new Date(existing.updatedAt)) {
        map.set(d.recordingId, d);
      }
    }
    return map;
  }, [drafts]);

  const handleGenerate = async (recordingId: string) => {
    setGenerating(recordingId);
    setError(null);
    try {
      const res = await apiPost<{ draft: Draft }>("/api/scoping-emails/generate", {
        recordingId,
        projectId: generateOptions.projectId || null,
        includeEstimate: generateOptions.includeEstimate,
      });
      refetchDrafts();
      setEditing(res.draft);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGenerating(null);
    }
  };

  const handleOpenDraft = async (id: number) => {
    try {
      const res = await fetch(`/api/scoping-emails/${id}`);
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as { draft: Draft };
      setEditing(data.draft);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (editing) {
    return (
      <ScopingEmailEditor
        draft={editing}
        onClose={() => {
          setEditing(null);
          refetchDrafts();
        }}
        onChange={(d) => setEditing(d)}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Scoping Emails</h1>
        <p>Generate client-facing scope confirmation emails from meeting transcripts.</p>
      </div>

      {error && (
        <div className="card" style={{ borderLeft: "4px solid var(--color-danger)" }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>Generate options</h2>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="form-group" style={{ marginBottom: 0, flex: "1 1 240px" }}>
            <label>Project (optional, used for codebase context)</label>
            <SearchSelect
              options={[
                { value: 0, label: "No project" },
                ...(projects || []).map((p) => ({ value: p.id, label: p.name })),
              ]}
              value={generateOptions.projectId}
              onChange={(v) =>
                setGenerateOptions((o) => ({ ...o, projectId: v as number }))
              }
              placeholder="Select project..."
            />
          </div>
          <label
            style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
          >
            <input
              type="checkbox"
              checked={generateOptions.includeEstimate}
              onChange={(e) =>
                setGenerateOptions((o) => ({ ...o, includeEstimate: e.target.checked }))
              }
            />
            Include hour estimates
          </label>
        </div>
      </div>

      {drafts.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2>Your drafts</h2>
          </div>
          <table>
            <thead>
              <tr>
                <th>Meeting</th>
                <th>Status</th>
                <th>Estimate</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((d) => (
                <tr key={d.id}>
                  <td>{d.recordingTitle || d.subject || "Untitled"}</td>
                  <td>
                    <span className={`badge badge-${d.status === "sent" ? "success" : "info"}`}>
                      {d.status}
                    </span>
                    {d.confidence && d.confidence !== "high" && (
                      <span
                        className={`badge badge-${d.confidence === "low" ? "danger" : "warning"}`}
                        style={{ marginLeft: 6 }}
                      >
                        {d.confidence} confidence
                      </span>
                    )}
                  </td>
                  <td>
                    {d.includeEstimate && d.totalLowHours != null
                      ? `${d.totalLowHours}–${d.totalHighHours}h`
                      : "—"}
                  </td>
                  <td>{new Date(d.updatedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleOpenDraft(d.id)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>Recent meetings</h2>
        </div>
        {recordings.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)" }}>
            No meetings found. Make sure your Fellow API key is set in Settings.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recordings.map((r) => {
                const existing = draftByRecording.get(r.id);
                return (
                  <tr key={r.id}>
                    <td>{r.title || "Untitled meeting"}</td>
                    <td>{new Date(r.started_at).toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>
                      {existing ? (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleOpenDraft(existing.id)}
                        >
                          Open draft
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-primary"
                          disabled={generating === r.id}
                          onClick={() => handleGenerate(r.id)}
                        >
                          {generating === r.id ? "Generating..." : "Generate"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {generating && (
        <LoadingOverlay message="Drafting scope email with AI... This may take a moment." />
      )}
    </div>
  );
}

interface EditorProps {
  draft: Draft;
  onClose: () => void;
  onChange: (d: Draft) => void;
}

function ScopingEmailEditor({ draft, onClose, onChange }: EditorProps) {
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "rich" | "plain">("idle");

  const totals = useMemo(() => {
    const low = draft.scopeItems.reduce((s, i) => s + (i.estimateLowHours || 0), 0);
    const high = draft.scopeItems.reduce((s, i) => s + (i.estimateHighHours || 0), 0);
    return { low, high };
  }, [draft.scopeItems]);

  const update = (patch: Partial<Draft>) => onChange({ ...draft, ...patch });

  const updateScopeItem = (idx: number, patch: Partial<ScopeItem>) => {
    const next = draft.scopeItems.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    update({ scopeItems: next });
  };

  const addScopeItem = () => {
    update({
      scopeItems: [
        ...draft.scopeItems,
        {
          name: "",
          description: "",
          assumptions: [],
          estimateLowHours: 0,
          estimateHighHours: 0,
        },
      ],
    });
  };

  const removeScopeItem = (idx: number) => {
    update({ scopeItems: draft.scopeItems.filter((_, i) => i !== idx) });
  };

  const save = useCallback(
    async (override?: Partial<Draft>) => {
      setSaving(true);
      try {
        const body = {
          subject: draft.subject || "",
          intro: draft.intro || "",
          scopeItems: draft.scopeItems,
          outOfScope: draft.outOfScope,
          closing: draft.closing || "",
          signOffAsk: draft.signOffAsk || "",
          totalLowHours: totals.low,
          totalHighHours: totals.high,
          recipientEmails: draft.recipientEmails,
          includeEstimate: draft.includeEstimate,
          ...(override || {}),
        };
        const res = await apiPut<{ draft: Draft }>(`/api/scoping-emails/${draft.id}`, body);
        onChange(res.draft);
        setSavedAt(new Date());
      } catch (err) {
        alert("Failed to save: " + (err as Error).message);
      } finally {
        setSaving(false);
      }
    },
    [draft, totals, onChange]
  );

  const markSent = async () => {
    await save({ status: "sent" } as Partial<Draft>);
  };

  const handleDelete = async () => {
    if (!confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await apiDelete(`/api/scoping-emails/${draft.id}`);
      onClose();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const previewHtml = useMemo(() => renderEmailHtml(draft, totals), [draft, totals]);
  const previewText = useMemo(() => renderEmailText(draft, totals), [draft, totals]);

  const copyRich = async () => {
    try {
      const blobHtml = new Blob([previewHtml], { type: "text/html" });
      const blobText = new Blob([previewText], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": blobHtml, "text/plain": blobText }),
      ]);
      setCopyState("rich");
      setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      // Fallback: copy plain
      await navigator.clipboard.writeText(previewText);
      setCopyState("plain");
      setTimeout(() => setCopyState("idle"), 1500);
    }
  };

  const copyPlain = async () => {
    await navigator.clipboard.writeText(previewText);
    setCopyState("plain");
    setTimeout(() => setCopyState("idle"), 1500);
  };

  return (
    <div>
      <div
        className="page-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}
      >
        <div>
          <button
            className="btn btn-sm btn-secondary"
            onClick={onClose}
            style={{ marginBottom: 8 }}
          >
            ← Back to list
          </button>
          <h1 style={{ marginBottom: 4 }}>{draft.recordingTitle || "Scoping email"}</h1>
          <p style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span className={`badge badge-${draft.status === "sent" ? "success" : "info"}`}>
              {draft.status}
            </span>
            {draft.confidence && (
              <span
                className={`badge badge-${
                  draft.confidence === "high"
                    ? "success"
                    : draft.confidence === "low"
                    ? "danger"
                    : "warning"
                }`}
              >
                {draft.confidence} confidence
              </span>
            )}
            {savedAt && (
              <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            className="btn btn-secondary"
            onClick={() => save()}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          {draft.status !== "sent" && (
            <button className="btn btn-success" onClick={markSent} disabled={saving}>
              Mark sent
            </button>
          )}
          <button className="btn btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>

      {draft.warnings.length > 0 && (
        <div
          className="card"
          style={{ borderLeft: "4px solid var(--color-warning)" }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Review before sending</div>
          <ul style={{ paddingLeft: 20 }}>
            {draft.warnings.map((w, i) => (
              <li key={i} style={{ fontSize: 13 }}>
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 16,
        }}
      >
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Email content</h2>
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input
                type="text"
                value={draft.subject || ""}
                onChange={(e) => update({ subject: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Intro</label>
              <textarea
                rows={4}
                value={draft.intro || ""}
                onChange={(e) => update({ intro: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Recipients (informational — paste into your email client)</label>
              <input
                type="text"
                value={draft.recipientEmails.join(", ")}
                onChange={(e) =>
                  update({
                    recipientEmails: e.target.value
                      .split(",")
                      .map((s: string) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={draft.includeEstimate}
                  onChange={(e) => update({ includeEstimate: e.target.checked })}
                />
                Include hour estimates in email
              </label>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Scope items</h2>
              <button className="btn btn-sm btn-secondary" onClick={addScopeItem}>
                + Add item
              </button>
            </div>
            {draft.scopeItems.length === 0 && (
              <p style={{ color: "var(--color-text-muted)" }}>
                No scope items. The transcript may not have produced any concrete
                deliverables — review the warnings above or add items manually.
              </p>
            )}
            <div className="task-preview-list">
              {draft.scopeItems.map((item, idx) => (
                <ScopeItemEditor
                  key={idx}
                  item={item}
                  includeEstimate={draft.includeEstimate}
                  onChange={(patch) => updateScopeItem(idx, patch)}
                  onRemove={() => removeScopeItem(idx)}
                />
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Out of scope</h2>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => update({ outOfScope: [...draft.outOfScope, ""] })}
              >
                + Add
              </button>
            </div>
            {draft.outOfScope.length === 0 && (
              <p style={{ color: "var(--color-text-muted)" }}>
                Nothing flagged as out of scope.
              </p>
            )}
            {draft.outOfScope.map((item, idx) => (
              <div
                key={idx}
                style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}
              >
                <input
                  type="text"
                  value={item}
                  onChange={(e) => {
                    const next = [...draft.outOfScope];
                    next[idx] = e.target.value;
                    update({ outOfScope: next });
                  }}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius)",
                  }}
                />
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() =>
                    update({ outOfScope: draft.outOfScope.filter((_, i) => i !== idx) })
                  }
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Closing</h2>
            </div>
            <div className="form-group">
              <label>Closing</label>
              <textarea
                rows={2}
                value={draft.closing || ""}
                onChange={(e) => update({ closing: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Sign-off ask</label>
              <textarea
                rows={2}
                value={draft.signOffAsk || ""}
                onChange={(e) => update({ signOffAsk: e.target.value })}
              />
            </div>
            {draft.includeEstimate && (
              <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
                Total estimate (auto-summed from items): <strong>{totals.low}–{totals.high}h</strong>
              </div>
            )}
          </div>
        </div>

        <div style={{ position: "sticky", top: 16, alignSelf: "flex-start" }}>
          <div className="card">
            <div className="card-header">
              <h2>Preview</h2>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-sm btn-primary" onClick={copyRich}>
                  {copyState === "rich" ? "Copied!" : "Copy rich"}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={copyPlain}>
                  {copyState === "plain" ? "Copied!" : "Copy plain"}
                </button>
              </div>
            </div>
            <div
              className="call-notes-output"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface ScopeItemEditorProps {
  item: ScopeItem;
  includeEstimate: boolean;
  onChange: (patch: Partial<ScopeItem>) => void;
  onRemove: () => void;
}

function ScopeItemEditor({ item, includeEstimate, onChange, onRemove }: ScopeItemEditorProps) {
  return (
    <div className="task-card">
      <div className="task-card-header">
        <div style={{ flex: 1 }}>
          <input
            className="task-edit-name"
            value={item.name}
            placeholder="Scope item name"
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </div>
        <button className="btn btn-sm btn-danger" onClick={onRemove}>
          Remove
        </button>
      </div>
      <textarea
        className="task-edit-desc"
        rows={3}
        value={item.description}
        placeholder="Description (client-facing)"
        onChange={(e) => onChange({ description: e.target.value })}
      />

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Assumptions</div>
        {item.assumptions.map((a, i) => (
          <div
            key={i}
            style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}
          >
            <input
              type="text"
              value={a}
              onChange={(e) => {
                const next = [...item.assumptions];
                next[i] = e.target.value;
                onChange({ assumptions: next });
              }}
              style={{
                flex: 1,
                padding: "6px 10px",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius)",
                fontSize: 13,
              }}
            />
            <button
              className="btn btn-sm btn-secondary"
              onClick={() =>
                onChange({ assumptions: item.assumptions.filter((_, j) => j !== i) })
              }
            >
              ×
            </button>
          </div>
        ))}
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => onChange({ assumptions: [...item.assumptions, ""] })}
          style={{ marginTop: 4 }}
        >
          + Add assumption
        </button>
      </div>

      {includeEstimate && (
        <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Estimate:</span>
          <input
            type="number"
            min={0}
            value={item.estimateLowHours}
            onChange={(e) => onChange({ estimateLowHours: Number(e.target.value) || 0 })}
            style={{
              width: 70,
              padding: "4px 8px",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius)",
            }}
          />
          <span>–</span>
          <input
            type="number"
            min={0}
            value={item.estimateHighHours}
            onChange={(e) => onChange({ estimateHighHours: Number(e.target.value) || 0 })}
            style={{
              width: 70,
              padding: "4px 8px",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius)",
            }}
          />
          <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>hours</span>
        </div>
      )}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function paragraphs(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

function renderEmailHtml(d: Draft, totals: { low: number; high: number }): string {
  const parts: string[] = [];
  if (d.intro) parts.push(paragraphs(d.intro));

  if (d.scopeItems.length > 0) {
    parts.push(`<h3>Scope</h3>`);
    for (const item of d.scopeItems) {
      parts.push(`<p><strong>${escapeHtml(item.name)}</strong>`);
      if (d.includeEstimate && (item.estimateLowHours || item.estimateHighHours)) {
        parts.push(
          ` <span style="color:#64748b">(${item.estimateLowHours}–${item.estimateHighHours} hours)</span>`
        );
      }
      parts.push(`</p>`);
      if (item.description) {
        parts.push(`<p>${escapeHtml(item.description).replace(/\n/g, "<br/>")}</p>`);
      }
      const assumptions = item.assumptions.filter((a) => a.trim());
      if (assumptions.length > 0) {
        parts.push(`<p style="margin-bottom:4px"><em>Assumptions:</em></p>`);
        parts.push(
          `<ul>${assumptions.map((a) => `<li>${escapeHtml(a)}</li>`).join("")}</ul>`
        );
      }
    }
  }

  const oos = d.outOfScope.filter((s) => s.trim());
  if (oos.length > 0) {
    parts.push(`<h3>Not included in this scope</h3>`);
    parts.push(`<ul>${oos.map((s) => `<li>${escapeHtml(s)}</li>`).join("")}</ul>`);
  }

  if (d.includeEstimate && (totals.low || totals.high)) {
    parts.push(
      `<p><strong>Estimated total effort:</strong> ${totals.low}–${totals.high} hours</p>`
    );
  }

  if (d.closing) parts.push(paragraphs(d.closing));
  if (d.signOffAsk) parts.push(paragraphs(d.signOffAsk));

  return parts.join("\n");
}

function renderEmailText(d: Draft, totals: { low: number; high: number }): string {
  const lines: string[] = [];
  if (d.intro) lines.push(d.intro, "");

  if (d.scopeItems.length > 0) {
    lines.push("Scope", "");
    for (const item of d.scopeItems) {
      let header = item.name;
      if (d.includeEstimate && (item.estimateLowHours || item.estimateHighHours)) {
        header += ` (${item.estimateLowHours}–${item.estimateHighHours} hours)`;
      }
      lines.push(header);
      if (item.description) lines.push(item.description);
      const assumptions = item.assumptions.filter((a) => a.trim());
      if (assumptions.length > 0) {
        lines.push("Assumptions:");
        for (const a of assumptions) lines.push(`  - ${a}`);
      }
      lines.push("");
    }
  }

  const oos = d.outOfScope.filter((s) => s.trim());
  if (oos.length > 0) {
    lines.push("Not included in this scope:");
    for (const s of oos) lines.push(`  - ${s}`);
    lines.push("");
  }

  if (d.includeEstimate && (totals.low || totals.high)) {
    lines.push(`Estimated total effort: ${totals.low}–${totals.high} hours`, "");
  }

  if (d.closing) lines.push(d.closing, "");
  if (d.signOffAsk) lines.push(d.signOffAsk);

  return lines.join("\n").trim();
}
